/**
 * LeftShield Portal - Control Detail Page
 * Route: /controls/:referenceId | Category: Compliance
 *
 * #12772 — completes the last Feature #12746 drawer→route conversion. The inline
 * `ControlDetailDrawer` on `ControlCoveragePage` is replaced by this GUID-routed page
 * now that `GET /portal/mcp/controls/detail/{referenceId}` is portal-reachable (see the
 * resolved limitation noted on `ControlDetail.referenceId` in `src/types/index.ts`).
 *
 * PARITY NOTES — the inline drawer rendered from the framework's control-list response
 * (typed `ComplianceControl`, with a few extra properties such as `isEnabled` available
 * at runtime but outside that TS shape): status badge, severity badge, Control ID,
 * Framework badge, Category, Findings count, Last Assessed, Name, Description. This page
 * reads the dedicated, richer `ControlDetail` DTO instead, which does not carry `status`,
 * `findingCount`, `lastAssessedAt`, or `frameworkCode` — those are per-framework /
 * per-assessment fields that don't exist on the control-catalog record the new endpoint
 * returns. Every field that IS present on `ControlDetail` is ported: `severity`,
 * `isEnabled` (status equivalent), `controlId`, `name`, `description`, `category`,
 * `section`, `policyRuleName`, `implementationGuidance`, `testingProcedures`,
 * `referenceId`. No separate "mappings" collection exists on the `ControlDetail`
 * contract (`src/types/index.ts`) to port — nothing else was omitted.
 */
import React from 'react';
import {
  Title2,
  Body1,
  Caption1,
  Card,
  Badge,
  Button,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { ShieldCheckmarkRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePageStyles } from '../../styles/page-styles';
import { PageHeader } from '../../components/common/PageHeader';
import { complianceApi } from '../../services/api';
import { DetailSection, DetailField, DetailBadges, DetailMetadataGrid } from '../../components/common/DetailDrawer';

/** Compliance Cyan — `brand/brand-config.json` → `colors.categoryThemes.compliance.primary`. */
const CONTROL_COLOR = '#13B5B1';

const severityNames = ['Info', 'Low', 'Medium', 'High', 'Critical'] as const;

const getSeverityName = (severity: string | number): string => {
  if (typeof severity === 'number') return severityNames[severity] ?? 'Info';
  return severity;
};

const getSeverityColor = (severity: string | number): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (getSeverityName(severity).toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

export const ControlDetailPage: React.FC = () => {
  const ps = usePageStyles();
  const { referenceId } = useParams<{ referenceId: string }>();
  const navigate = useNavigate();

  const {
    data: control,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['control-detail', referenceId],
    queryFn: () => complianceApi.getControlDetailByReferenceId(referenceId!),
    enabled: !!referenceId,
  });

  const backAction = (
    <Button appearance="subtle" onClick={() => navigate('/control-coverage')}>
      Back to Control Coverage
    </Button>
  );

  if (!referenceId) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Control Detail"
          subtitle="No control reference ID was provided."
          icon={<ShieldCheckmarkRegular />}
          iconColor={CONTROL_COLOR}
          actions={backAction}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Control Detail"
          subtitle="Loading enforcement control…"
          icon={<ShieldCheckmarkRegular />}
          iconColor={CONTROL_COLOR}
        />
        <Card>
          <div className={ps.cardBody}>
            <Spinner label="Loading control…" />
          </div>
        </Card>
      </div>
    );
  }

  if (isError || !control) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Control Detail"
          subtitle={`Control ${referenceId}`}
          icon={<ShieldCheckmarkRegular />}
          iconColor={CONTROL_COLOR}
          actions={backAction}
        />
        <Card>
          <div className={ps.cardBody}>
            <ErrorCircleRegular style={{ fontSize: '32px', color: tokens.colorPaletteRedForeground1 }} />
            <Body1>Unable to load this control. It may not exist, or you may not have access to it.</Body1>
            <Button appearance="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const c = control;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Control Detail"
        subtitle={`Enforcement control — ${c.name}`}
        icon={<ShieldCheckmarkRegular />}
        iconColor={CONTROL_COLOR}
        actions={backAction}
      />

      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>{c.name}</Title2>
            <Badge appearance="outline">{c.controlId}</Badge>
          </div>

          <DetailBadges>
            <Badge appearance="filled" color={c.isEnabled ? 'success' : 'informative'}>
              {c.isEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Badge appearance="filled" color={getSeverityColor(c.severity)}>
              {getSeverityName(c.severity)}
            </Badge>
          </DetailBadges>

          <DetailMetadataGrid>
            <DetailField label="Control ID">
              <Body1 style={{ fontFamily: 'monospace', fontWeight: 500 }}>{c.controlId}</Body1>
            </DetailField>
            {c.category && (
              <DetailField label="Category">
                <Body1>{c.category}</Body1>
              </DetailField>
            )}
            {c.section && (
              <DetailField label="Section">
                <Body1>{c.section}</Body1>
              </DetailField>
            )}
            {c.policyRuleName && (
              <DetailField label="Policy">
                <Body1 style={{ fontFamily: 'monospace' }}>{c.policyRuleName}</Body1>
              </DetailField>
            )}
          </DetailMetadataGrid>

          {c.description && (
            <DetailSection title="Description" divider>
              <Body1>{c.description}</Body1>
            </DetailSection>
          )}

          {c.implementationGuidance && (
            <DetailSection title="Implementation Guidance" divider>
              <Body1>{c.implementationGuidance}</Body1>
            </DetailSection>
          )}

          {c.testingProcedures && (
            <DetailSection title="Testing Procedures" divider>
              <Body1>{c.testingProcedures}</Body1>
            </DetailSection>
          )}

          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Reference ID: {c.referenceId}
          </Caption1>
        </div>
      </Card>
    </div>
  );
};
